import { useLogger } from 'hook/error.hook';
import { useDispatch, useSelector } from 'react-redux';
import {
  getNotificationsDetails,
  setViewNotificationAlert,
} from 'actions/notificationsAlerts.actions';
import { useEffect } from 'react';
import {
  getConfigForCSIDAlert,
  getCsidAttribute,
  getWebAttribute,
} from 'pages/Alerts/types/csid.alert.constants';
import {
  getConfigForAlert,
  getValueForAlertDetailsAttribute,
} from 'helpers/alerts.helpers';
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';
import { updateSessionTimeoutInLocal } from 'helpers/session';
import { setCsidViewAlert } from 'actions/csidAlerts.actions';
import { setViewAlert } from 'actions/cmAlerts.actions';
import { useConfig } from 'hook/config.hook';
import {
  SSN_TRACE_ALERT,
  SSN_TRACE_REPORT,
} from 'pages/Alerts/types/csid.constants';
import { formatNotificationAlertDate } from 'helpers/dateHelper';
import { cmAlertTypes } from 'types/cm.alert.constants';

const reportBureauName = {
  EXPERIAN: 'Experian',
  TRANS_UNION: 'TransUnion',
  EQUIFAX: 'Equifax',
  CS_ETL: 'CreditSesame',
};

export const useNotificationsAlertsDetails = props => {
  const logger = useLogger({ name: 'notificationsAlertsDetails.hook' });
  const dispatch = useDispatch();
  const { config } = useConfig();

  const {
    userInfo,
    selectedAlert,
    selectedAlertDetails,
    selectedAlertDetailsLoading,
    selectedAlertDetailsError,
    goals,
    configData,
  } = useSelector(state => {
    return {
      userInfo: state?.userInfo?.userInfo,
      selectedAlert: state?.notificationsAlerts?.selectedAlert,
      selectedAlertDetails:
        state?.notificationsAlerts?.selectedAlertsDetails?.selectedAlertDetails,
      selectedAlertDetailsLoading:
        state?.notificationsAlerts?.selectedAlertsDetails
          ?.selectedAlertDetailsLoading,
      selectedAlertDetailsError:
        state?.notificationsAlerts?.selectedAlertsDetails
          ?.selectedAlertDetailsError,
      goals: state?.goals?.goals,
      configData: config,
    };
  });

  const buildAlertDetailsList = (
    configTitles,
    getAttributeHelper,
    responseDetails,
    category,
    alertDetailsResponse,
    type,
  ) => {
    let configHolder = [];
    configTitles.forEach(item => {
      configHolder.push({
        title: item,
        value: getAttributeHelper(
          item,
          responseDetails,
          category,
          alertDetailsResponse,
          type,
        ),
      });
    });
    return configHolder;
  };

  const createAlertDetailsConfig = (
    alertDetailsResponse,
    csidFlag,
    webFlag,
  ) => {
    const {
      darkWebFlag,
      nameChangeWebFlag,
      addressChangeWebFlag,
      courtRecordWebFlag,
    } = webFlag || {};
    let alertDetailsList = [];
    let alertMultiDetailLists = [];
    let alertMultiRecordsLists = [];
    let alertMultiDetailsRecordsLists = [];
    const detailConfig = csidFlag ? getConfigForCSIDAlert : getConfigForAlert;
    let getAttributeHelper = getValueForAlertDetailsAttribute;
    let alertType = '';
    if (csidFlag) {
      alertType = darkWebFlag
        ? 'Dark Web'
        : nameChangeWebFlag
        ? 'Name Change'
        : addressChangeWebFlag
        ? 'Change of Address'
        : '';
      getAttributeHelper = getCsidAttribute;
      if (
        darkWebFlag ||
        nameChangeWebFlag ||
        addressChangeWebFlag ||
        courtRecordWebFlag
      ) {
        getAttributeHelper = getWebAttribute;
      }
    }

    let reportType = 'tu',
      categoryConfig = 'category',
      details = 'alertDetails',
      bureauName = reportBureauName[alertDetailsResponse?.bureau];

    if (csidFlag) {
      reportType = 'csid';
      categoryConfig = 'alertCategory';
      details = 'alertInfo';
    }
    const category = alertDetailsResponse[categoryConfig];
    let config = detailConfig(category, userInfo, configData);

    if (config && alertDetailsResponse[details]) {
      // Alerts from TU return additional alerts details
      // Other Experian/Equifax returns an iframe source
      const notifiedDate = alertDetailsResponse?.notifiedDate;
      const alertDetails = {
        ...alertDetailsResponse[details],
        notifiedDate,
        alertType,
      };

      if (csidFlag || bureauName === 'TransUnion') {
        let titles = config.detailTitles;
        if (titles) {
          if (config.multiDetailAndRecordsList) {
            const loans = alertDetails?.report?.inquiry?.pda?.detail?.loan;
            const inquiries = alertDetails?.report?.inquiry?.pda?.detail?.inq;
            let loansRecords = [];
            let inquiriesRecords = [];

            titles?.forEach(title => {
              if (title?.header === 'Inquiries' && inquiries?.length > 0) {
                inquiries.forEach((item, index) => {
                  let inquiryRecordItem = {};
                  inquiryRecordItem = buildAlertDetailsList(
                    title?.titles,
                    getAttributeHelper,
                    alertDetails,
                    category,
                    item,
                    title?.header,
                  );
                  if (index === 0) {
                    inquiryRecordItem.header = title?.header;
                  }
                  inquiriesRecords.push(inquiryRecordItem);
                });
              }
              if (title?.header === 'Loans' && loans?.length > 0) {
                loans?.forEach((item, index) => {
                  let loanRecordItem = {};
                  loanRecordItem = buildAlertDetailsList(
                    title?.titles,
                    getAttributeHelper,
                    alertDetails,
                    category,
                    item,
                    title?.header,
                  );
                  if (index === 0) {
                    loanRecordItem.header = title?.header;
                  }
                  loansRecords.push(loanRecordItem);
                });
              }
            });
            alertMultiDetailsRecordsLists.push(loansRecords, inquiriesRecords);
          } else if (config.multiRecordsList) {
            let dataPoint = alertDetails?.report?.record;

            if (category === SSN_TRACE_REPORT || category === SSN_TRACE_ALERT) {
              dataPoint = alertDetails?.transaction?.data;
            }
            dataPoint?.forEach(item => {
              alertDetailsList = buildAlertDetailsList(
                titles,
                getAttributeHelper,
                alertDetails,
                category,
                item,
              );
              alertMultiRecordsLists.push(alertDetailsList);
            });
          } else if (config?.multiDetailList) {
            titles.forEach(item => {
              let listItem = {};
              listItem.header = item.header;
              listItem.alertDetailsList = buildAlertDetailsList(
                item.titles,
                getAttributeHelper,
                alertDetails,
                category,
                alertDetailsResponse,
              );

              alertMultiDetailLists.push(listItem);
            });
          } else {
            alertDetailsList = buildAlertDetailsList(
              titles,
              getAttributeHelper,
              alertDetails,
              category,
              alertDetailsResponse,
            );
          }
        }

        config.updatedScore = alertDetails.totalCount ?? null;

        if (config.updatedScore && alertDetails.tuAdditionalInformation) {
          if (alertDetails.tuAdditionalInformation.ChangedBy) {
            config.scoreChange = alertDetails.tuAdditionalInformation.ChangedBy;
          } else if (alertDetails.tuAdditionalInformation.OldScore) {
            config.scoreChange =
              alertDetails.totalCount -
              alertDetails.tuAdditionalInformation.OldScore;
          }
        }
      } else if (typeof alertDetailsResponse[details] === 'string') {
        config.description =
          config.description &&
          config.description.replace('TransUnion®', bureauName);
        alertDetailsList = alertDetailsResponse[details];
      }
    }

    config.alertDetailsList = alertDetailsList;
    config.alertMultiDetailLists = alertMultiDetailLists;
    config.alertMultiRecordsLists = alertMultiRecordsLists;
    config.alertMultiDetailsRecordsLists = alertMultiDetailsRecordsLists;
    config.category = category;
    config.reportType = reportType;
    config.bureauName = bureauName;
    config.date = formatNotificationAlertDate(
      alertDetailsResponse?.createDate ?? alertDetailsResponse?.alertDate,
    );
    if (
      category === cmAlertTypes.ATTEMPTED_INQUIRY_FREEZE &&
      alertDetailsResponse?.alertDetails?.requesterName
    ) {
      config.requesterName = alertDetailsResponse?.alertDetails?.requesterName;
    }

    return config;
  };

  const alertDetailsConfig = selectedAlertDetails
    ? createAlertDetailsConfig(
        selectedAlertDetails,
        selectedAlertDetails?.notificationType === 'ID_PROTECTION',
        {
          darkWebFlag:
            selectedAlertDetails?.alertCategory === 'DARK_WEB_MONITORING',
          nameChangeWebFlag:
            selectedAlertDetails?.alertCategory === 'NAME_CHANGE',
          addressChangeWebFlag:
            selectedAlertDetails?.alertCategory === 'ADDRESS_CHANGE',
          courtRecordWebFlag:
            selectedAlertDetails?.alertCategory === 'CRIMINAL_ACTIVITY',
        },
      )
    : null;

  useEffect(() => {
    if (
      selectedAlert &&
      selectedAlertDetails?.id !== selectedAlert?.id &&
      !selectedAlertDetailsLoading
    ) {
      getNotificationsAlertsDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAlert]);

  const getNotificationsAlertsDetails = () => {
    dispatch(
      getNotificationsDetails(
        selectedAlert?.notificationType,
        selectedAlert?.id,
      ),
    )
      .then(res => {
        updateSessionTimeoutInLocal();
        if (!res?.viewed && !res?.viewedDate) {
          const detailsSetViewAction = setView(res?.notificationType);
          dispatch(detailsSetViewAction(res?.id, true, res?.notificationType));
        }
      })

      .catch(err => {
        logger.showError(err?.message);
        logger.reportAPIError(err);
        logger.reportAPIError('Unable to Load Alerts Details', err);
      });
  };

  const setView = notificationType => {
    switch (notificationType) {
      case 'CREDIT_MONITORING':
        return setViewAlert;
      case 'ID_PROTECTION':
        return setCsidViewAlert;
      default:
        return setViewNotificationAlert;
    }
  };

  const detailsLinkClick = extraAttrs => {
    props.eventHandler(CLICK, {
      'Click Type': extraAttrs.clickType,
      'Page Name': extraAttrs.detailsPageName,
      __alert: true,
    });
  };

  return {
    selectedAlertDetails,
    selectedAlertDetailsLoading,
    selectedAlertDetailsError,
    showNewTemplate:
      selectedAlertDetails?.notificationType === 'CREDIT_MONITORING',
    detailsLinkClick,
    alertDetailsConfig,
    goals,
  };
};
